import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import { Alert, Appearance } from 'react-native';

// General Context for app-wide settings and utilities
const GeneralContext = createContext();

// Action types
const GENERAL_ACTIONS = {
  SET_THEME: 'SET_THEME',
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_NETWORK_STATUS: 'SET_NETWORK_STATUS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
  UPDATE_APP_SETTINGS: 'UPDATE_APP_SETTINGS',
  SET_PERFORMANCE_METRICS: 'SET_PERFORMANCE_METRICS',
  UPDATE_CACHE: 'UPDATE_CACHE',
  SET_ACCESSIBILITY: 'SET_ACCESSIBILITY',
  SET_PRIVACY_SETTINGS: 'SET_PRIVACY_SETTINGS',
  UPDATE_SYNC_STATUS: 'UPDATE_SYNC_STATUS',
};

// Initial state
const initialState = {
  // Theme and appearance
  theme: 'light', // 'light', 'dark', 'auto'
  colorScheme: 'default',
  
  // Language and localization
  language: 'en',
  region: 'US',
  
  // Network and connectivity
  isConnected: true,
  connectionType: 'wifi',
  networkStrength: 'strong',
  
  // App state
  isLoading: false,
  error: null,
  notification: null,
  
  // Settings
  appSettings: {
    notifications: {
      push: true,
      email: true,
      sms: false,
      marketing: false,
    },
    privacy: {
      analytics: true,
      crashReporting: true,
      locationTracking: false,
      dataSharing: false,
    },
    accessibility: {
      fontSize: 'medium',
      highContrast: false,
      screenReader: false,
      reducedMotion: false,
    },
    performance: {
      dataUsage: 'normal',
      cacheSize: 'medium',
      backgroundSync: true,
      offlineMode: false,
    },
  },
  
  // Performance metrics
  performanceMetrics: {
    appStartTime: Date.now(),
    screenLoadTimes: {},
    apiResponseTimes: {},
    errorCount: 0,
    crashCount: 0,
  },
  
  // Cache management
  cache: {
    images: {},
    data: {},
    lastCleared: Date.now(),
    size: 0,
  },
  
  // Sync status
  syncStatus: {
    lastSync: null,
    pendingSync: false,
    syncErrors: [],
  },
};

// Reducer
const generalReducer = (state, action) => {
  switch (action.type) {
    case GENERAL_ACTIONS.SET_THEME:
      return {
        ...state,
        theme: action.payload,
      };
      
    case GENERAL_ACTIONS.SET_LANGUAGE:
      return {
        ...state,
        language: action.payload.language,
        region: action.payload.region || state.region,
      };
      
    case GENERAL_ACTIONS.SET_NETWORK_STATUS:
      return {
        ...state,
        isConnected: action.payload.isConnected,
        connectionType: action.payload.type,
        networkStrength: action.payload.strength || state.networkStrength,
      };
      
    case GENERAL_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
      
    case GENERAL_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        performanceMetrics: {
          ...state.performanceMetrics,
          errorCount: state.performanceMetrics.errorCount + 1,
        },
      };
      
    case GENERAL_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
      
    case GENERAL_ACTIONS.SET_NOTIFICATION:
      return {
        ...state,
        notification: action.payload,
      };
      
    case GENERAL_ACTIONS.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: null,
      };
      
    case GENERAL_ACTIONS.UPDATE_APP_SETTINGS:
      return {
        ...state,
        appSettings: {
          ...state.appSettings,
          ...action.payload,
        },
      };
      
    case GENERAL_ACTIONS.SET_PERFORMANCE_METRICS:
      return {
        ...state,
        performanceMetrics: {
          ...state.performanceMetrics,
          ...action.payload,
        },
      };
      
    case GENERAL_ACTIONS.UPDATE_CACHE:
      return {
        ...state,
        cache: {
          ...state.cache,
          ...action.payload,
        },
      };
      
    case GENERAL_ACTIONS.SET_ACCESSIBILITY:
      return {
        ...state,
        appSettings: {
          ...state.appSettings,
          accessibility: {
            ...state.appSettings.accessibility,
            ...action.payload,
          },
        },
      };
      
    case GENERAL_ACTIONS.SET_PRIVACY_SETTINGS:
      return {
        ...state,
        appSettings: {
          ...state.appSettings,
          privacy: {
            ...state.appSettings.privacy,
            ...action.payload,
          },
        },
      };
      
    case GENERAL_ACTIONS.UPDATE_SYNC_STATUS:
      return {
        ...state,
        syncStatus: {
          ...state.syncStatus,
          ...action.payload,
        },
      };
      
    default:
      return state;
  }
};

// Provider component
export const GeneralProvider = ({ children }) => {
  const [state, dispatch] = useReducer(generalReducer, initialState);
  const [toastQueue, setToastQueue] = useState([]);

  // Initialize app settings from storage
  useEffect(() => {
    loadAppSettings();
    setupNetworkListener();
    setupThemeListener();
    trackAppStart();
  }, []);

  // Load saved settings
  const loadAppSettings = async () => {
    try {
      const savedSettings = await AsyncStorage.getItem('app_settings');
      const savedTheme = await AsyncStorage.getItem('app_theme');
      const savedLanguage = await AsyncStorage.getItem('app_language');
      
      if (savedSettings) {
        dispatch({
          type: GENERAL_ACTIONS.UPDATE_APP_SETTINGS,
          payload: JSON.parse(savedSettings),
        });
      }
      
      if (savedTheme) {
        dispatch({
          type: GENERAL_ACTIONS.SET_THEME,
          payload: savedTheme,
        });
      }
      
      if (savedLanguage) {
        const languageData = JSON.parse(savedLanguage);
        dispatch({
          type: GENERAL_ACTIONS.SET_LANGUAGE,
          payload: languageData,
        });
      }
    } catch (error) {
      console.error('Error loading app settings:', error);
    }
  };

  // Setup network connectivity listener
  const setupNetworkListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      dispatch({
        type: GENERAL_ACTIONS.SET_NETWORK_STATUS,
        payload: {
          isConnected: state.isConnected,
          type: state.type,
          strength: state.details?.strength || 'unknown',
        },
      });
    });

    return unsubscribe;
  };

  // Setup theme change listener
  const setupThemeListener = () => {
    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      if (state.theme === 'auto') {
        dispatch({
          type: GENERAL_ACTIONS.SET_THEME,
          payload: colorScheme,
        });
      }
    });

    return () => subscription?.remove();
  };

  // Track app start performance
  const trackAppStart = () => {
    const startTime = Date.now();
    dispatch({
      type: GENERAL_ACTIONS.SET_PERFORMANCE_METRICS,
      payload: {
        appStartTime: startTime,
      },
    });
  };

  // Context methods
  const setTheme = async (theme) => {
    try {
      await AsyncStorage.setItem('app_theme', theme);
      dispatch({
        type: GENERAL_ACTIONS.SET_THEME,
        payload: theme,
      });
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  };

  const setLanguage = async (language, region) => {
    try {
      const languageData = { language, region };
      await AsyncStorage.setItem('app_language', JSON.stringify(languageData));
      dispatch({
        type: GENERAL_ACTIONS.SET_LANGUAGE,
        payload: languageData,
      });
    } catch (error) {
      console.error('Error saving language:', error);
    }
  };

  const updateAppSettings = async (settings) => {
    try {
      const newSettings = {
        ...state.appSettings,
        ...settings,
      };
      await AsyncStorage.setItem('app_settings', JSON.stringify(newSettings));
      dispatch({
        type: GENERAL_ACTIONS.UPDATE_APP_SETTINGS,
        payload: settings,
      });
    } catch (error) {
      console.error('Error updating app settings:', error);
    }
  };

  const showToast = (message, type = 'info', duration = 3000) => {
    const toast = {
      id: Date.now(),
      message,
      type, // 'success', 'error', 'warning', 'info'
      duration,
    };
    
    dispatch({
      type: GENERAL_ACTIONS.SET_NOTIFICATION,
      payload: toast,
    });

    // Auto-clear toast
    setTimeout(() => {
      dispatch({
        type: GENERAL_ACTIONS.CLEAR_NOTIFICATION,
      });
    }, duration);
  };

  const showAlert = (title, message, buttons = []) => {
    Alert.alert(title, message, buttons);
  };

  const setLoading = (loading) => {
    dispatch({
      type: GENERAL_ACTIONS.SET_LOADING,
      payload: loading,
    });
  };

  const setError = (error) => {
    dispatch({
      type: GENERAL_ACTIONS.SET_ERROR,
      payload: error,
    });
  };

  const clearError = () => {
    dispatch({
      type: GENERAL_ACTIONS.CLEAR_ERROR,
    });
  };

  const trackScreenLoad = (screenName, loadTime) => {
    dispatch({
      type: GENERAL_ACTIONS.SET_PERFORMANCE_METRICS,
      payload: {
        screenLoadTimes: {
          ...state.performanceMetrics.screenLoadTimes,
          [screenName]: loadTime,
        },
      },
    });
  };

  const trackApiCall = (endpoint, responseTime) => {
    dispatch({
      type: GENERAL_ACTIONS.SET_PERFORMANCE_METRICS,
      payload: {
        apiResponseTimes: {
          ...state.performanceMetrics.apiResponseTimes,
          [endpoint]: responseTime,
        },
      },
    });
  };

  const trackUserAction = (action, metadata = {}) => {
    if (state.appSettings.privacy.analytics) {
      // Track user action for analytics
      console.log('User Action:', action, metadata);
    }
  };

  const clearCache = async () => {
    try {
      // Clear specific cache items
      await AsyncStorage.multiRemove(['image_cache', 'data_cache']);
      
      dispatch({
        type: GENERAL_ACTIONS.UPDATE_CACHE,
        payload: {
          images: {},
          data: {},
          lastCleared: Date.now(),
          size: 0,
        },
      });
      
      showToast('Cache cleared successfully', 'success');
    } catch (error) {
      console.error('Error clearing cache:', error);
      showToast('Failed to clear cache', 'error');
    }
  };

  const updateSyncStatus = (status) => {
    dispatch({
      type: GENERAL_ACTIONS.UPDATE_SYNC_STATUS,
      payload: status,
    });
  };

  const setAccessibilitySettings = (settings) => {
    dispatch({
      type: GENERAL_ACTIONS.SET_ACCESSIBILITY,
      payload: settings,
    });
  };

  const setPrivacySettings = (settings) => {
    dispatch({
      type: GENERAL_ACTIONS.SET_PRIVACY_SETTINGS,
      payload: settings,
    });
  };

  // Context value
  const value = {
    // State
    ...state,
    
    // Theme methods
    setTheme,
    
    // Language methods
    setLanguage,
    
    // Settings methods
    updateAppSettings,
    setAccessibilitySettings,
    setPrivacySettings,
    
    // UI methods
    showToast,
    showAlert,
    setLoading,
    setError,
    clearError,
    
    // Performance methods
    trackScreenLoad,
    trackApiCall,
    trackUserAction,
    
    // Cache methods
    clearCache,
    
    // Sync methods
    updateSyncStatus,
  };

  return (
    <GeneralContext.Provider value={value}>
      {children}
    </GeneralContext.Provider>
  );
};

// Hook to use General context
export const useGeneral = () => {
  const context = useContext(GeneralContext);
  if (!context) {
    throw new Error('useGeneral must be used within a GeneralProvider');
  }
  return context;
};

export default GeneralContext;




